import type { UIMessage } from 'ai';
import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';

import { AiChatTransport } from '../services/ai-chat-transport';

interface AiChatContextType {
  messages: UIMessage[];
  sendMessage: (content: string | { parts: any[] }) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  startTyping: () => void;
  stopTyping: () => void;
}

const AiChatContext = createContext<AiChatContextType | null>(null);

interface AiChatProviderProps {
  children: ReactNode;
  owner: string;
  repo: string;
  pullNumber: number;
}

export function AiChatProvider({ children, owner, repo, pullNumber }: AiChatProviderProps) {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const transportRef = useRef<AiChatTransport | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>(
    'disconnected'
  );

  // Initialize transport and connect immediately when component mounts
  useEffect(() => {
    const threadId = `${owner}/${repo}/${pullNumber}`;
    const wsHost = import.meta.env.DEV ? 'localhost:3000' : window.location.host;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    const transport = new AiChatTransport({
      url: `${protocol}//${wsHost}/api/ws/${owner}/${repo}/${pullNumber}`,
      threadId,
    });

    transportRef.current = transport;

    // Set up message handler
    const unsubscribe = transport.onMessage((message) => {
      setMessages((prev) => {
        // Find if message already exists (for streaming updates)
        const existingIndex = prev.findIndex((m) => m.id === message.id);

        if (existingIndex >= 0) {
          // Update existing message
          const updated = [...prev];
          updated[existingIndex] = message;
          return updated;
        } else {
          // Add new message
          return [...prev, message];
        }
      });
    });

    // Set up loading state handler
    const unsubscribeLoading = transport.onLoadingChange((loading) => {
      setIsLoading(loading);
    });

    // Connect immediately when PR page loads
    transport.connect().catch((err) => {
      console.error('Failed to connect WebSocket:', err);
      setError('Failed to connect to chat service');
    });

    // Monitor connection status
    const interval = setInterval(() => {
      const state = transport.getConnectionState();
      if (state !== connectionStatus) {
        setConnectionStatus(state);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      unsubscribe();
      unsubscribeLoading();
      transport.disconnect();
    };
  }, [owner, repo, pullNumber, connectionStatus]);

  const sendMessage = useCallback(async (content: string | { parts: any[] }) => {
    if (!transportRef.current) {
      setError('Transport not initialized');
      return;
    }

    try {
      setError(null);

      // Create UIMessage compatible format
      const message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'user' as const,
        parts: typeof content === 'string' ? [{ type: 'text', text: content }] : content.parts,
        metadata: { timestamp: new Date().toISOString() },
      };

      await transportRef.current.sendMessage(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    }
  }, []);

  const startTyping = useCallback(() => {
    transportRef.current?.startTyping();
  }, []);

  const stopTyping = useCallback(() => {
    transportRef.current?.stopTyping();
  }, []);

  const contextValue: AiChatContextType = {
    messages,
    sendMessage,
    isLoading,
    error,
    connectionStatus,
    startTyping,
    stopTyping,
  };

  return <AiChatContext.Provider value={contextValue}>{children}</AiChatContext.Provider>;
}

export function useAiChat(): AiChatContextType {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error('useAiChat must be used within an AiChatProvider');
  }
  return context;
}
